import { useEffect, useState } from "react";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import { useNavigate } from "react-router";
import {
  Button,
  Card,
  Flex,
  Form,
  Grid,
  Input,
  message,
  Pagination,
  Select,
  Table,
  Tag,
} from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { waterTankService } from "../../services/water-tank.service";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

export default function WaterTankIndex() {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight();

  const [messageApi, messageHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [filterForm] = Form.useForm();

  const monthMap = {
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    10: "October",
    11: "November",
    12: "December",
  };

  const currentMonth =
    monthMap[String(new Date().getMonth() + 1).padStart(2, "0")];
  const currentYear = String(new Date().getFullYear());

  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    month: currentMonth,
    year: currentYear,
    keyword: "",
  });

  // Set title
  useEffect(() => {
    setTitle("Air Tangki");
  }, [setTitle]);

  useEffect(() => {
    filterForm.setFieldsValue({
      month: String(new Date().getMonth() + 1).padStart(2, "0"),
      year: currentYear,
    });
  }, [filterForm, currentYear]);

  const month = Form.useWatch("month", filterForm);
  const year = Form.useWatch("year", filterForm);

  useEffect(() => {
    const monthValue =
      month || String(new Date().getMonth() + 1).padStart(2, "0");
    const monthLabel = monthMap[monthValue] || currentMonth;
    const yearValue = year || currentYear;

    setDataParams((prev) => ({
      ...prev,
      month: monthLabel,
      year: yearValue,
      keyword: debouncedKeyword || "",
    }));
  }, [debouncedKeyword, month, year, currentMonth, currentYear]);

  const {
    data: waterTankData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["waterTank", dataParams],
    queryFn: () => waterTankService.getAllData(dataParams),
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (page, pageSize) => {
    setDataParams((prev) => ({
      ...prev,
      limit: pageSize,
      page,
    }));
  };

  const mappedData =
    waterTankData?.data?.map((item) => ({
      ...item,
      name: item.Nomen?.name || "-",
      nomen: item.Nomen?.nomen || "-",
      email: item.Customer?.email || "-",
      createdAt: item.requestDate || item.createdAt,
      index: item.index,
      regional: item.Nomen?.area || "-",
      ab: item.Nomen?.ab || "-",
      status: item.status,
    })) || [];

  const columns = [
    {
      title: "No.",
      align: "center",
      width: 80,
      fixed: breakpoint.md && "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Nama Pelanggan",
      dataIndex: "nama_kustomer",
      width: 230,
      render: (_, record) => (
        <div>
          <strong>{record.name}</strong> <br /> {record.nomen}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 200,
    },
    {
      title: "Tanggal Input",
      dataIndex: "createdAt",
      width: 160,
      render: (val) => {
        if (!val) return "-";
        const date = format(new Date(val), "dd/MM/yyyy");
        const time = format(new Date(val), "HH.mm");

        return (
          <div>
            <strong>{date}</strong>
            <br />
            {time}
          </div>
        );
      },
    },
    {
      title: "Regional",
      dataIndex: "regional",
      width: 200,
    },
    {
      title: "AB",
      dataIndex: "ab",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (val) => {
        if (val === "verified") {
          return <Tag color="blue">Verified</Tag>;
        }
        if (val === "rejected") {
          return <Tag color="red">Cancel</Tag>;
        }
        return "-";
      },
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      fixed: "right",
      width: 80,
      render: (record) => (
        <Button
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="primary"
          variant="filled"
          onClick={() => navigate("/water-tank/detail/" + record.id)}
        />
      ),
    },
  ];

  return (
    <>
      {messageHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Form form={filterForm}>
          <Flex
            justify="space-between"
            align="center"
            style={{ paddingInline: 24, marginBottom: 24 }}
          >
            <Input
              style={{ width: 250 }}
              prefix={
                <Icon icon="hugeicons:search-01" width="16" height="16" />
              }
              placeholder="Cari..."
              onChange={(e) => setKeyword(e.target.value)}
              value={keyword}
            />

            <Flex gap={20}>
              <Form.Item noStyle name="month">
                <Select
                  allowClear
                  placeholder="Bulan"
                  style={{ width: 150 }}
                  options={[
                    { label: "Januari", value: "01" },
                    { label: "Februari", value: "02" },
                    { label: "Maret", value: "03" },
                    { label: "April", value: "04" },
                    { label: "Mei", value: "05" },
                    { label: "Juni", value: "06" },
                    { label: "Juli", value: "07" },
                    { label: "Agustus", value: "08" },
                    { label: "September", value: "09" },
                    { label: "Oktober", value: "10" },
                    { label: "November", value: "11" },
                    { label: "Desember", value: "12" },
                  ]}
                />
              </Form.Item>

              <Form.Item noStyle name="year">
                <Select
                  allowClear
                  placeholder="Tahun"
                  style={{ width: 120 }}
                  options={Array.from({ length: 6 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return { label: `${year}`, value: `${year}` };
                  })}
                />
              </Form.Item>
            </Flex>
          </Flex>
        </Form>

        <Table
          id="registration-table"
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={"id"}
          scroll={{ y: tableHeight }}
          dataSource={mappedData}
          pagination={false}
          rowClassName={(record) => (!record.is_open ? "row-open" : "")}
        />

        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={waterTankData?.pagination?.total}
          showSizeChanger
          pageSizeOptions={["10", "20", "50"]}
          current={waterTankData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
    </>
  );
}
